import React, { useRef, useEffect, useState } from "react";
import {
  Users,
  UserPlus,
  Building2,
  HandCoins,
  Upload,
  Mail,
} from "lucide-react";
import { DataTable, Button, Typography } from "../../components/common";
import Loading from "../../components/common/Loading";
import StaffStatusFilter from "./StaffStatusFilter";
import RosterExportDialog from "./RosterExportDialog";
import ActivationConfirmDialog from "./ActivationConfirmDialog";
import InviteUserDialog from "./InviteUserDialog";
import RemoveStaffAccessDialog from "./RemoveStaffAccessDialog";
import BulkUploadDialog from "./BulkUploadDialog";
import OrderCancellationCard from "./OrderCancellationCard";
import CompetencyMetricsPanel from "./CompetencyMetricsPanel";
import CompletionReminders from "./CompletionReminders";
import { useOrganisationDetailsHook } from "./useOrganisationDetailsHook";
import { useUserProfile } from "../../context/UserProfileContext";
import { Feature, useEntitlements } from "../../context/EntitlementsContext";

const EXPANSION_CONTACT_URL =
  "mailto:hello@pedagogue.io?subject=Existing%20school%20licence%20enquiry";

const OrganisationDetails: React.FC = () => {
  const {
    // State
    searchInput, statusFilter, serverSearchAvailable, filtersActive,
    unfilteredTotalCount, statusCounts, stagedNotPermitted, usersError, exportingRoster,
    handleSearchInputChange, handleStatusFilterChange, handleExportRoster, activationBatchTotal,
    organisation,
    users,
    loading,
    error,
    usersLoading,
    inviteDialogOpen,
    inviteLoading,
    bulkUploadDialogOpen,
    currentPage,
    pageSize,
    totalCount,
    notActivatedCount,
    selectedUserIds,
    activationInFlight,
    staffActionsBlocked,
    removalTarget,
    removalPending,
    removalError,
    removalNeedsRefresh,
    handleCloseRemovalDialog,
    handleRemoveStaffAccess,
    activationProgress,
    activationStopRequested,
    handleStopActivation,
    activationRefreshFailed,
    activationRecoveryMessage,
    organisationId,
    // Actions
    handleInviteUser,
    handleCloseInviteDialog,
    handleInviteSubmit,
    handleBulkUpload,
    handleCloseBulkUploadDialog,
    handleBulkUploadSuccess,
    handleBulkActivate,
    handleRefreshOrganisationUsers,
    handlePageChange,
    handlePageSizeChange,
    setSelectedUserIds,
    canManageLifecycleTarget,
    // Utility
    userColumns,
    isReadOnly,
    canWriteStaffLifecycle,
  } = useOrganisationDetailsHook();

  const staffTableRef = useRef<HTMLDivElement>(null);
  const removalTriggerRef = useRef<HTMLElement | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [activationConfirmation, setActivationConfirmation] = useState<{
    scope: "selected" | "all"; count: number; userIds?: string[]; selectionKey?: string;
  } | null>(null);
  useEffect(() => {
    setActivationConfirmation(null);
    setExportDialogOpen(false);
  }, [users, currentPage, pageSize, organisationId, notActivatedCount, searchInput, statusFilter]);
  const selectionKey = JSON.stringify(Array.from(selectedUserIds).sort());
  const selectedConfirmationChanged = activationConfirmation?.scope === "selected" &&
    activationConfirmation.selectionKey !== selectionKey;
  useEffect(() => {
    if (selectedConfirmationChanged) setActivationConfirmation(null);
  }, [selectedConfirmationChanged]);
  const confirmActivation = () => {
    if (!activationConfirmation || selectedConfirmationChanged || !canWriteStaffLifecycle || staffActionsBlocked || usersError) return;
    const confirmation = activationConfirmation;
    setActivationConfirmation(null);
    handleBulkActivate(confirmation.scope === "all", confirmation.userIds);
  };
  const { userProfile } = useUserProfile();
  const { hasFeature } = useEntitlements();
  const canBulkInvite = hasFeature(Feature.BULK_INVITE);
  const canExportRoster = hasFeature(Feature.COMPLIANCE_EXPORTS) && ["admin", "organisation_admin", "platform_admin", "billing_admin", "super_admin"].includes(userProfile?.role || "");
  const bulkUploadDialog = (
    <BulkUploadDialog
      open={bulkUploadDialogOpen}
      onClose={handleCloseBulkUploadDialog}
      organisation={organisation}
      onSuccess={handleBulkUploadSuccess}
    />
  );

  if (loading) {
    return (
      <div className="w-full h-full">
        {bulkUploadDialog}
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full">
        {bulkUploadDialog}
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <Typography variant="p" color="error">
            {error}
          </Typography>
        </div>
        <Typography variant="p" color="default">
          Please contact your administrator if you believe this is an error.
        </Typography>
      </div>
    );
  }

  if (!organisation) {
    return (
      <div className="w-full h-full">
        {bulkUploadDialog}
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <Typography variant="p" color="warning">
            Organisation not found
          </Typography>
        </div>
        <Typography variant="p" color="default">
          The requested organisation could not be found.
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      {bulkUploadDialog}
      {/* Card Title */}
      <div className="mb-4">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 max-sm:items-start max-sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shrink-0 p-3 bg-primary/10 rounded-lg">
              <Building2 size={24} className="text-green-500" />
            </div>
            <div className="min-w-0 [overflow-wrap:anywhere]">
              <div className="font-display text-2xl font-bold text-gray-900 mb-1">
                {organisation.name}
              </div>
              <div className="text-sm text-gray-500">
                Manage your organization users and licenses
              </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3 max-sm:w-full max-sm:gap-4">
            <div className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-50 border border-blue-200">
              <Users size={16} className="mr-2" />
              <Typography
                variant="p"
                weight="medium"
                className="!text-blue-600 !text-sm"
              >
                Licenses Used:
              </Typography>
              <Typography
                variant="p"
                weight="semibold"
                className="!text-blue-600 !text-sm"
              >
                {organisation.used_licences} of {organisation.total_licences}
              </Typography>
            </div>
            <div className="flex items-center gap-1 px-4 py-2 rounded-lg bg-green-50 border border-green-200">
              <HandCoins size={16} className="text-green-600 mr-2" />
              <Typography
                variant="p"
                weight="medium"
                color="success"
                className="!text-sm"
              >
                Available Licenses:
              </Typography>
              <Typography
                variant="p"
                weight="semibold"
                color="success"
                className="!text-sm"
              >
                {organisation.total_licences - organisation.used_licences}
              </Typography>
            </div>
            <a
              href={EXPANSION_CONTACT_URL}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-green-500 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <Mail size={18} aria-hidden="true" />
              Contact us about more licences
            </a>
          </div>
        </div>
      </div>

      {/* Published journey and Analytics access are independently feature-gated in the panel. */}
      {organisationId && (
        <CompetencyMetricsPanel organisationId={organisationId} />
      )}

      {/* Read-only banner for billing users and cross-organisation super admins. */}
      {isReadOnly && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <span className="text-amber-600 text-sm font-medium">
            You have read-only access to this organisation
          </span>
        </div>
      )}

      {activationRefreshFailed && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between gap-3"
        >
          <Typography variant="p" color="error">
            {activationRecoveryMessage}
          </Typography>
          <Button
            variant="outline"
            onClick={handleRefreshOrganisationUsers}
            className="!normal-case !px-3 !py-2 !rounded-lg !font-medium !text-sm"
          >
            Refresh staff list
          </Button>
        </div>
      )}

      {usersError && <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
        <span>Could not load the staff list.</span>
        <Button variant="outline" onClick={handleRefreshOrganisationUsers} disabled={usersLoading}>Try again</Button>
      </div>}
      {filtersActive && <p className="mb-3 text-sm" aria-live="polite">Showing {totalCount} of {unfilteredTotalCount} staff</p>}

      {activationInFlight && activationProgress && (
        <div className="mb-4 flex flex-wrap items-center gap-3" role="status" aria-live="polite">
          <span>{serverSearchAvailable === true
            ? `Activating staff… batch ${Math.min(activationProgress.batchIndex + 1, activationBatchTotal)} of ${activationBatchTotal} (${activationProgress.activatedSoFar} activated so far)`
            : `Activating staff… ${activationProgress.activatedSoFar} activated so far`}</span>
          <Button variant="outline" disabled={activationStopRequested} onClick={handleStopActivation}>
            Stop after this batch
          </Button>
        </div>
      )}

      {/* Users Table */}
      <RemoveStaffAccessDialog
        target={removalTarget}
        pending={removalPending}
        error={removalError}
        needsRefresh={removalNeedsRefresh}
        refreshing={usersLoading}
        onClose={handleCloseRemovalDialog}
        onConfirm={handleRemoveStaffAccess}
        onRefresh={handleRefreshOrganisationUsers}
        onExited={() => {
          const trigger = removalTriggerRef.current;
          removalTriggerRef.current = null;
          if (trigger?.isConnected) {
            trigger.focus();
            return;
          }
          staffTableRef.current?.focus();
        }}
      />

      {/* Keep actions discoverable without changing the shared table. */}
      <div
        ref={staffTableRef}
        tabIndex={-1}
        onClickCapture={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          const trigger = target.closest("button");
          if (trigger instanceof HTMLElement) removalTriggerRef.current = trigger;
        }}
        aria-label="Staff table"
        className="min-w-0 [&>div>div:first-child>div]:flex-wrap [&>div>div:first-child>div>div:first-child>div]:!min-w-0 [&>div>div:first-child>div>div:first-child>div]:w-full [&>div>div:last-child>div]:flex-wrap [&>div>div:last-child>div>div:first-child]:flex-wrap [&>div>div:last-child>div>div:first-child]:gap-2 [&_.overflow-x-hidden]:!overflow-x-auto [&_th:last-child]:sticky [&_th:last-child]:right-0 [&_th:last-child]:bg-gray-50 [&_td:last-child]:sticky [&_td:last-child]:right-0 [&_td:last-child]:bg-white [&_td:last-child]:z-10"
      >
      <DataTable
        columns={userColumns}
        data={users}
        loading={usersLoading}
        error={null}
        searchable={true}
        searchPlaceholder="Search users..."
        searchFields={["full_name", "email"]}
        search={{value: searchInput, onChange: handleSearchInputChange, disabled: serverSearchAvailable !== true,
          title: serverSearchAvailable !== true ? "Search is unavailable until the service is updated" : undefined}}
        toolbar={<StaffStatusFilter counts={statusCounts} selected={statusFilter} onChange={handleStatusFilterChange} disabled={serverSearchAvailable !== true} />}
        stale={usersError}
        emptyMessage={filtersActive ? "No staff match this search or filter" : "No users found"}
        pagination={{
          totalCount,
          page: currentPage - 1, // MUI pagination is 0-based
          pageSize,
          onPageChange: (page) => handlePageChange(page + 1), // Convert back to 1-based
          onPageSizeChange: handlePageSizeChange,
        }}
        selection={
          canWriteStaffLifecycle
            ? {
                selectedIds: selectedUserIds,
                onSelectedIdsChange: setSelectedUserIds,
                isRowSelectable: (row) =>
                  row.status === "not_activated" && !row.activation_recovery_pending && !usersError && !usersLoading &&
                  canManageLifecycleTarget(row),
                renderBulkActions: (selectedRows) => (
                  <div
                    className="flex items-center gap-2"
                    aria-label="Staff activation actions"
                  >
                    {selectedRows.length > 0 && (
                      <Button
                        variant="outline"
                        onClick={() =>
                          setActivationConfirmation({
                            scope: "selected", count: selectedRows.length,
                            userIds: selectedRows.map((row) => row.id),
                            selectionKey,
                          })
                        }
                        disabled={staffActionsBlocked || usersError}
                        className="!normal-case !px-3 !py-2 !rounded-lg !font-medium !text-sm"
                      >
                        {activationRefreshFailed
                          ? "Refresh staff list to continue"
                          : activationInFlight
                            ? "Activating staff..."
                            : `Activate selected (${selectedRows.length})`}
                      </Button>
                    )}
                    {notActivatedCount > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => setActivationConfirmation({ scope: "all", count: notActivatedCount })}
                        disabled={staffActionsBlocked || usersError}
                        className="!normal-case !px-3 !py-2 !rounded-lg !font-medium !text-sm"
                      >
                        {activationRefreshFailed
                          ? "Refresh staff list to continue"
                          : activationInFlight
                            ? "Activating staff..."
                            : `Activate permitted staged staff (${notActivatedCount})`}
                      </Button>
                    )}
                  </div>
                ),
              }
            : undefined
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {canExportRoster && <Button variant="outline" disabled={usersLoading || usersError || exportingRoster} onClick={() => setExportDialogOpen(true)}>
              Export staff roster (CSV)
            </Button>}
            {canWriteStaffLifecycle && canBulkInvite && (
              <Button
                variant="outline"
                startIcon={<Upload size={20} />}
                onClick={handleBulkUpload}
                disabled={staffActionsBlocked}
                className="!normal-case !px-4 !py-2 !rounded-lg !font-medium !text-sm"
              >
                Bulk upload staff
              </Button>
            )}
            {canWriteStaffLifecycle && (
              <Button
                variant="filled"
                startIcon={<UserPlus size={20} />}
                onClick={handleInviteUser}
                disabled={staffActionsBlocked}
                className="!normal-case !px-4 !py-2 !rounded-lg !font-medium !text-sm"
              >
                Invite User
              </Button>
            )}
          </div>
        }
      />
      </div>

      {organisationId && (
        <CompletionReminders
          organisationId={organisationId}
          readOnly={isReadOnly}
        />
      )}

      <ActivationConfirmDialog
        open={activationConfirmation !== null}
        scope={activationConfirmation?.scope ?? "selected"}
        count={activationConfirmation?.count ?? 0}
        organisationName={organisation.name}
        filteredCount={filtersActive ? totalCount : undefined}
        stagedNotPermitted={stagedNotPermitted}
        disabled={activationInFlight || usersLoading || usersError || !canWriteStaffLifecycle}
        onCancel={() => setActivationConfirmation(null)}
        onConfirm={confirmActivation}
      />

      <RosterExportDialog open={exportDialogOpen} count={totalCount} filtered={filtersActive}
        busy={exportingRoster || usersLoading || usersError} onCancel={() => setExportDialogOpen(false)}
        onDownload={async () => { if (await handleExportRoster()) setExportDialogOpen(false); }} />

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        organisation={organisation}
        onSubmit={handleInviteSubmit}
        loading={inviteLoading}
        actorRole={userProfile?.role}
      />

      {/* G2b: order & cancellation card (renders nothing without a
          self-serve order). */}
      <OrderCancellationCard />
    </div>
  );
};

export default OrganisationDetails;
